/**
 * «Επιλογές» του προϊόντος: σύνδεση/αποσύνδεση ΚΟΙΝΩΝ ομάδων επιλογών και σειρά τους στο ταμείο.
 * Απόφαση ιδιοκτήτη 12/10/2026: μια υπάρχουσα ομάδα ΔΕΝ αλλάζει από εδώ (αλλάζει σε όλα τα προϊόντα)
 * — μόνο από την καρτέλα «Ομάδες επιλογών»· εδώ φτιάχνεται μόνο νέα, στη θέση της, με την Αποθήκευση.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, ExternalLink, GripVertical, Link2, Loader2, Plus, Trash2, X } from 'lucide-react';
import { ApiOptionGroup, GroupRef } from './catalogModel';
import type { NewOptionGroup, ProductState } from './catalogDraft';
import { cx, MenuButton, MoneyInput, Switch } from './ui';
import { DragState, dropHalf, moveItem } from './dragState';

export interface GroupInfo {
  ref: GroupRef;
  name: string;
  selection_type: 'single' | 'multiple';
  is_required: boolean;
  choices: Array<{ name: string; is_default?: boolean }>;
  productCount: number | null;
  isNew: boolean;
}

export const groupInfo = (g: ApiOptionGroup): GroupInfo => ({
  ref: g.id, name: g.name, selection_type: g.selection_type, is_required: g.is_required, choices: g.choices || [],
  productCount: g.product_count !== undefined && g.product_count !== null ? Number(g.product_count) : null, isNew: false,
});

export const newGroupInfo = (g: NewOptionGroup): GroupInfo => ({
  ref: g.ref, name: g.name, selection_type: g.selection_type, is_required: g.is_required, choices: g.choices, productCount: null, isNew: true,
});

export interface NewGroupDraft {
  name: string;
  selection_type: 'single' | 'multiple';
  is_required: boolean;
  choices: Array<{ name: string; price_modifier: number; is_default: boolean }>;
}

export interface OptionsTabProps {
  productKey: string;
  state: ProductState;
  groups: GroupInfo[];
  /** οι συνδέσεις του προϊόντος φορτώθηκαν (ή είναι νέο) */
  loaded: boolean;
  changed: Set<string>;
  readOnly: boolean;
  categoryName: string;
  othersInCategory: number;
  drag: DragState;
  onChange: (fn: (s: ProductState) => ProductState) => void;
  onCreateGroup: (group: NewGroupDraft, alsoOthers: boolean) => void;
  onOpenLibrary: () => void;
}

const preview = (g: GroupInfo, max = 4) => {
  const shown = g.choices.slice(0, max);
  return (
    <>
      {shown.map((c, i) => (
        <React.Fragment key={i}>{i > 0 && ' · '}{c.is_default ? <b>{c.name}</b> : c.name}</React.Fragment>
      ))}
      {g.choices.length > max && ` · +${g.choices.length - max}`}
    </>
  );
};

const EMPTY_GROUP: NewGroupDraft = { name: '', selection_type: 'single', is_required: false, choices: [] };

const ProductOptionsTab: React.FC<OptionsTabProps> = props => {
  const { t } = useTranslation(['catalog']);
  const { state, groups, readOnly, drag, productKey } = props;
  const linked: GroupRef[] = state.groups || [];
  const byRef = new Map(groups.map(g => [String(g.ref), g] as [string, GroupInfo]));
  const [form, setForm] = useState<NewGroupDraft | null>(null);
  const [alsoOthers, setAlsoOthers] = useState(false);
  const [pendingChoice, setPendingChoice] = useState('');
  const [over, setOver] = useState<{ ref: string; pos: 'before' | 'after' } | null>(null);
  const listId = `groups:${productKey}`;

  const setLinked = (fn: (list: GroupRef[]) => GroupRef[]) => props.onChange(s => ({ ...s, groups: fn(s.groups || []) }));
  const unlinked = groups.filter(g => !linked.some(r => String(r) === String(g.ref)));

  if (!props.loaded) {
    return <div className="cx-empty" role="status"><Loader2 className="cx-ico animate-spin" />{t('catalog:common.loading')}</div>;
  }

  const onDrop = (e: React.DragEvent, ref: string) => {
    e.preventDefault();
    const d = drag.current;
    const pos = over && over.ref === ref ? over.pos : dropHalf(e);
    setOver(null);
    drag.end();
    if (!d || d.kind !== 'row' || d.list !== listId || d.uid === ref) return;
    setLinked(list => {
      const keys = list.map(String);
      const from = keys.indexOf(d.uid);
      let to = keys.indexOf(ref);
      if (from < 0 || to < 0) return list;
      if (pos === 'after' && to < from) to += 1;
      if (pos === 'before' && to > from) to -= 1;
      return moveItem(list, from, to);
    });
  };

  /** Η επιλογή που γράφεται στην κενή γραμμή μπαίνει με Enter, έξοδο από το πεδίο ή «Προσθήκη». */
  const withPending = (f: NewGroupDraft): NewGroupDraft => (pendingChoice.trim()
    ? { ...f, choices: [...f.choices, { name: pendingChoice.trim(), price_modifier: 0, is_default: f.selection_type === 'single' && !f.choices.some(c => c.is_default) }] }
    : f);
  const commitPending = () => { if (form && pendingChoice.trim()) { setForm(withPending(form)); setPendingChoice(''); } };
  const validForm = !!form && !!form.name.trim() && (form.choices.some(c => c.name.trim()) || !!pendingChoice.trim());
  const setChoice = (i: number, patch: Partial<NewGroupDraft['choices'][number]>) => setForm(f => f && ({
    ...f,
    choices: f.choices.map((c, j) => {
      if (j === i) return { ...c, ...patch };
      // Μία επιλογή: ένα «προεπιλεγμένο».
      if (patch.is_default && f.selection_type === 'single') return { ...c, is_default: false };
      return c;
    }),
  }));

  return (
    <>
      <p className="cx-hint lead">{t('catalog:options.lead')}</p>
      <div className="cx-og-list" role="list" aria-label={t('catalog:options.linked')}>
        {linked.length === 0 && <div className="cx-empty" style={{ padding: 20 }}>{t('catalog:options.none')}</div>}
        {linked.map((ref, index) => {
          const g = byRef.get(String(ref));
          const name = g?.name || t('catalog:options.unknownGroup', { id: ref });
          const drop = over && over.ref === String(ref) ? over.pos : null;
          return (
            <div
              key={String(ref)}
              role="listitem"
              className={cx('cx-og', drop && `drop-${drop}`)}
              onDragOver={e => { if (drag.current?.kind === 'row' && drag.current.list === listId) { e.preventDefault(); setOver({ ref: String(ref), pos: dropHalf(e) }); } }}
              onDragLeave={() => setOver(o => (o && o.ref === String(ref) ? null : o))}
              onDrop={e => onDrop(e, String(ref))}
            >
              {!readOnly && (
                <button type="button" className="cx-grip" draggable aria-label={t('catalog:options.reorder', { name })} title={t('catalog:list.reorderHint')}
                  onDragStart={e => { drag.start({ kind: 'row', list: listId, uid: String(ref) }); try { e.dataTransfer.setData('text/plain', String(ref)); } catch { /* jsdom */ } }}
                  onDragEnd={() => { drag.end(); setOver(null); }}
                  onKeyDown={e => {
                    if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
                    e.preventDefault();
                    setLinked(list => moveItem(list, index, index + (e.key === 'ArrowUp' ? -1 : 1)));
                  }}>
                  <GripVertical className="cx-ico" />
                </button>
              )}
              <div className="cx-og-main">
                <div className="cx-og-t">
                  <b>{name}</b>
                  {g && <span className="cx-badge">{g.selection_type === 'single' ? t('catalog:options.single') : t('catalog:options.multiple')}</span>}
                  {g?.is_required && <span className="cx-badge cx-outline">{t('catalog:options.required')}</span>}
                  {g?.isNew && <span className="cx-badge brand">{t('catalog:status.new')}</span>}
                </div>
                {g && <div className="cx-og-c">{preview(g)}</div>}
              </div>
              {g && g.productCount !== null && (
                <span className="cx-og-u" title={t('catalog:options.usedIn', { count: g.productCount })}><Link2 className="cx-ico" />{g.productCount}</span>
              )}
              {g && !g.isNew && (
                <button type="button" className="cx-icon-btn sm" onClick={props.onOpenLibrary}
                  aria-label={t('catalog:options.openInLibrary', { name })} title={t('catalog:options.openInLibraryTitle', { count: g.productCount ?? 0 })}>
                  <ExternalLink className="cx-ico" />
                </button>
              )}
              {!readOnly && (
                <button type="button" className="cx-icon-btn sm" onClick={() => setLinked(list => list.filter(r => String(r) !== String(ref)))}
                  aria-label={t('catalog:options.unlink', { name })} title={t('catalog:options.unlink', { name })}>
                  <X className="cx-ico" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {!readOnly && (form ? (
        <div className="cx-ng" role="group" aria-label={t('catalog:options.newGroup')}>
          <div className="cx-ng-h"><span className="cx-badge brand">{t('catalog:options.newGroup')}</span><span className="cx-hint">{t('catalog:options.newGroupWhere')}</span></div>
          <div className="cx-ng-top">
            <div className="cx-field">
              <label htmlFor="ng-name">{t('catalog:fields.name')}</label>
              <input id="ng-name" className="cx-inp" autoFocus value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="cx-field">
              <span className="cx-lbl">{t('catalog:options.customerPicks')}</span>
              <div className="cx-seg sm" role="radiogroup" aria-label={t('catalog:options.customerPicks')}>
                {(['single', 'multiple'] as const).map(type => (
                  <button key={type} type="button" role="radio" aria-checked={form.selection_type === type}
                    onClick={() => setForm({
                      ...form,
                      selection_type: type,
                      // Από «Πολλές» σε «Μία»: κρατά μόνο το πρώτο προεπιλεγμένο.
                      choices: type === 'single'
                        ? form.choices.map((c, i) => ({ ...c, is_default: c.is_default && form.choices.findIndex(x => x.is_default) === i }))
                        : form.choices,
                    })}>
                    {type === 'single' ? t('catalog:options.single') : t('catalog:options.multiple')}
                  </button>
                ))}
              </div>
            </div>
            <div className="cx-field">
              <span className="cx-lbl">&nbsp;</span>
              <span className="cx-sw-field" title={t('catalog:options.requiredHint')}>
                <Switch checked={form.is_required} label={t('catalog:options.required')} onChange={next => setForm({ ...form, is_required: next })} />
                {t('catalog:options.required')}
              </span>
            </div>
          </div>
          <div className="cx-field">
            <span className="cx-lbl">{t('catalog:options.choices')}</span>
            <div className="cx-ch-tbl">
              <div className="cx-ch-h"><span /><span>{t('catalog:options.choice')}</span><span className="r">{t('catalog:options.extra')}</span><span className="c">{t('catalog:options.defaultShort')}</span><span /></div>
              {form.choices.map((c, i) => (
                <div key={i} className="cx-ch-r">
                  <span />
                  <input className="cx-inp sm" value={c.name} aria-label={t('catalog:options.choiceName', { index: i + 1 })}
                    onChange={e => setChoice(i, { name: e.target.value })} />
                  <div className="cx-sfx">
                    <MoneyInput value={c.price_modifier} label={t('catalog:options.choicePrice', { index: i + 1 })} placeholder="0,00"
                      onChange={v => setChoice(i, { price_modifier: v ?? 0 })} />
                    <span>€</span>
                  </div>
                  <span className="c">
                    <input type={form.selection_type === 'single' ? 'radio' : 'checkbox'} name="ng-default" checked={c.is_default}
                      aria-label={t('catalog:options.choiceDefault', { index: i + 1 })}
                      onChange={e => setChoice(i, { is_default: form.selection_type === 'single' ? true : e.target.checked })} />
                  </span>
                  <button type="button" className="cx-icon-btn sm" aria-label={t('catalog:options.removeChoice', { index: i + 1 })}
                    onClick={() => setForm({ ...form, choices: form.choices.filter((_, j) => j !== i) })}>
                    <Trash2 className="cx-ico" />
                  </button>
                </div>
              ))}
              <div className="cx-ch-r new">
                <span />
                <input className="cx-inp sm" value={pendingChoice} placeholder={t('catalog:options.newChoice')} aria-label={t('catalog:options.newChoice')}
                  onChange={e => setPendingChoice(e.target.value)}
                  onBlur={commitPending}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitPending(); } }} />
                <div className="cx-sfx"><input className="cx-inp sm num" placeholder="0,00" disabled aria-hidden="true" tabIndex={-1} /><span>€</span></div>
                <span />
                <span />
              </div>
            </div>
          </div>
          {props.othersInCategory > 0 && (
            <label className="cx-cb-row">
              <input type="checkbox" className="cx-cb" checked={alsoOthers} onChange={e => setAlsoOthers(e.target.checked)} />
              {t('catalog:options.alsoOthers', { count: props.othersInCategory, category: props.categoryName })}
            </label>
          )}
          <div className="cx-ng-foot">
            <span className="cx-hint">{t('catalog:options.savedWith')}</span>
            <button type="button" className="cx-btn ghost sm" onClick={() => { setForm(null); setPendingChoice(''); setAlsoOthers(false); }}>{t('catalog:common.cancel')}</button>
            <button
              type="button"
              className="cx-btn primary sm"
              disabled={!validForm}
              onClick={() => {
                if (!form) return;
                const full = withPending(form);
                props.onCreateGroup({
                  ...full,
                  name: full.name.trim(),
                  choices: full.choices.filter(c => c.name.trim()).map(c => ({ ...c, name: c.name.trim() })),
                }, alsoOthers);
                setForm(null);
                setPendingChoice('');
                setAlsoOthers(false);
              }}
            >
              <Check className="cx-ico" /> {t('catalog:common.add')}
            </button>
          </div>
        </div>
      ) : (
        <div className="cx-btn-row">
          <MenuButton
            label={t('catalog:options.addGroup')}
            className="cx-btn sm"
            disabled={unlinked.length === 0}
            title={unlinked.length === 0 ? t('catalog:options.allLinked') : t('catalog:options.addGroup')}
            items={unlinked.map(g => ({
              key: String(g.ref),
              label: g.name,
              hint: g.selection_type === 'single' ? t('catalog:options.single') : t('catalog:options.multiple'),
              onSelect: () => setLinked(list => [...list, g.ref]),
            }))}
          >
            <Plus className="cx-ico" /> {t('catalog:options.addGroup')} <ChevronDown className="cx-ico" />
          </MenuButton>
          <button type="button" className="cx-btn ghost sm" onClick={() => setForm({ ...EMPTY_GROUP, choices: [] })}>
            <Plus className="cx-ico" /> {t('catalog:options.newGroupButton')}
          </button>
        </div>
      ))}
    </>
  );
};

export default ProductOptionsTab;
